import type { MetricInput } from "@/types";
import { shortDate } from "@/lib/date";
import type { MetricView } from "./dashboard";
import {
  DENSE_AXIS,
  type Sparkline,
  recentPoints,
  sparkline,
  standing,
} from "./metricSeries";

const NO_READINGS = "No readings yet";
const STANDING_LABEL = {
  onTarget: "on target",
  offTarget: "off target",
  noTarget: "no target",
} as const;

const CADENCE_LABEL: Record<MetricInput["cadence"], string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
};

/** The metrics owned by one activity or one goal. Renders nothing when empty. */
export function MetricList({ metrics }: { metrics: MetricView[] }) {
  if (metrics.length === 0) return null;

  return (
    <ul className="metric-list">
      {metrics.map((view) => (
        <MetricItem key={view.metric.id} {...view} />
      ))}
    </ul>
  );
}

/**
 * One metric: its latest reading against its target, with the recent series as a
 * sparkline. Standing is never colour-alone — the value carries a text label.
 */
function MetricItem({ metric, points }: MetricView) {
  const { name, unit, target, cadence, direction } = metric.fields;
  const series = recentPoints({ metric, points });
  const latest = series.at(-1)?.fields.value;
  const state = standing(metric.fields, latest);
  const line = sparkline(series, target, direction);

  return (
    <li className="metric">
      <div className="metric-head">
        <span className="metric-name">{name}</span>
        <span className="metric-value">
          {latest === undefined ? (
            <span className="metric-empty">{NO_READINGS}</span>
          ) : (
            <>
              {formatValue(latest)} <span className="metric-unit">{unit}</span>
            </>
          )}
        </span>
      </div>

      {line !== null && <Chart line={line} name={name} unit={unit} />}

      <p className="metric-foot">
        {CADENCE_LABEL[cadence]}
        {target !== undefined && (
          <>
            {" · target "}
            {formatValue(target)} {unit}
            <span className="metric-standing" data-standing={state}>
              {STANDING_LABEL[state]}
            </span>
          </>
        )}
      </p>
    </li>
  );
}

/**
 * The plot. The SVG stretches to the card width (so x and y scale differently) and
 * carries only the line and the target; the readings are HTML dots positioned in
 * percent, which keeps them round at any width and gives each one a hover title.
 */
function Chart({ line, name, unit }: { line: Sparkline; name: string; unit: string }) {
  const lastIndex = line.markers.length - 1;
  const dense = line.markers.length > DENSE_AXIS;

  return (
    <div className="spark">
      <div className="spark-plot">
        <svg
          className="spark-svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          role="img"
          aria-label={`${name}: ${line.markers.length} readings, latest ${formatValue(
            line.markers[lastIndex]?.value ?? 0,
          )} ${unit}`}
        >
          {/* One gridline per period (day or week, per the metric's cadence), drawn
           * first so the series and the target sit on top of it. */}
          {line.markers.map((marker) => (
            <line
              key={marker.at}
              className="spark-grid"
              x1={marker.xPct}
              x2={marker.xPct}
              y1={0}
              y2={100}
            />
          ))}
          {line.targetYPct !== null && (
            <line
              className="spark-target"
              x1={0}
              x2={100}
              y1={line.targetYPct}
              y2={line.targetYPct}
            />
          )}
          <path className="spark-line" d={line.path} />
        </svg>
        {line.markers.map((marker, index) => (
          <span
            key={marker.at}
            className={index === lastIndex ? "spark-point is-last" : "spark-point"}
            style={{ left: `${marker.xPct}%`, top: `${marker.yPct}%` }}
            title={`${shortDate(marker.at)} — ${formatValue(marker.value)} ${unit}`}
          />
        ))}
        {/* Every reading prints its value, in muted ink — the mark carries identity,
         * the text never wears the series colour. Labels alternate above and below
         * the line so that neighbours occupy different bands and cannot collide. */}
        {line.markers.map((marker, index) => (
          <span
            key={marker.at}
            className={labelClass(index, lastIndex, marker.yPct)}
            style={{ left: `${marker.xPct}%`, top: `${marker.yPct}%` }}
          >
            {formatValue(marker.value)}
          </span>
        ))}
      </div>
      {/* X axis: one date per reading, sitting under the point it belongs to. The
       * readings are evenly spaced by period, so this is an ordinal axis, not a
       * continuous time scale. A dense series angles its labels to fit. */}
      <div className={dense ? "spark-axis is-dense" : "spark-axis"} aria-hidden="true">
        {line.ticks.map((tick) => (
          <span key={tick.xPct} className="spark-axis-tick" style={{ left: `${tick.xPct}%` }}>
            {tick.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/** A point below this line has no room underneath it — its label would land on the
 * x-axis dates. Above this one, there is no room overhead. */
const FLOOR_PCT = 62;
const CEILING_PCT = 20;

/**
 * A value label's placement: alternating bands (above / below) so neighbours never
 * overlap, plus an inward nudge at the ends, which would otherwise hang off the
 * plot's edges. The alternation yields to the plot's edges, though — a low point is
 * always labelled above (or it collides with the axis) and a high one below.
 * The latest reading sits above where it can, since that is where the eye lands.
 */
function labelClass(index: number, lastIndex: number, yPct: number): string {
  const band = bandFor(index, lastIndex, yPct);
  const edge = index === 0 ? " is-first" : index === lastIndex ? " is-last" : "";
  return `spark-point-value ${band}${edge}`;
}

function bandFor(index: number, lastIndex: number, yPct: number): "is-above" | "is-below" {
  if (yPct >= FLOOR_PCT) return "is-above";
  if (yPct <= CEILING_PCT) return "is-below";
  return index === lastIndex || index % 2 === 0 ? "is-above" : "is-below";
}

/** Readings are decimals like 22.4 or 6; drop the trailing ".0". */
function formatValue(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
